import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import './Gallery.css';

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [filter, setFilter] = useState('all');

  const galleryImages = [
    // Original 22 images for All Projects
    {
      id: 4,
      src: '/images/gallery/4.jpeg',
      alt: 'Solar Panel Installation',
      category: 'residential',
      title: 'Premium Home Solar Setup',
      description: 'High-efficiency monocrystalline solar panels with a 25-year warranty.'
    },
    {
      id: 5,
      src: '/images/gallery/5.jpeg',
      alt: 'Solar Energy Storage',
      category: 'storage',
      title: 'Battery Storage Solution',
      description: 'Advanced lithium-ion battery storage system with backup power capability.'
    },
    {
      id: 6,
      src: '/images/gallery/6.jpeg',
      alt: 'Commercial Building Solar',
      category: 'commercial',
      title: 'Office Building Solar Array',
      description: 'Integrated solar solution for a commercial office building.'
    },
    {
      id: 7,
      src: '/images/gallery/7.jpeg',
      alt: 'Solar Farm Development',
      category: 'industrial',
      title: 'Utility Scale Solar Farm',
      description: '10MW utility-scale solar farm with single-axis tracking systems.'
    },
    {
      id: 8,
      src: '/images/gallery/8.jpeg',
      alt: 'Rooftop Solar Array',
      category: 'residential',
      title: 'Suburban Home Solar Project',
      description: 'Complete rooftop solar solution with advanced micro-inverters.'
    },
    {
      id: 9,
      src: '/images/gallery/9.jpeg',
      alt: 'Solar Installation Team',
      category: 'residential',
      title: 'Professional Installation Service',
      description: 'Expert installation team ensuring safe and efficient mounting.'
    },
    {
      id: 10,
      src: '/images/gallery/10.jpeg',
      alt: 'Solar Maintenance',
      category: 'maintenance',
      title: 'Solar System Maintenance',
      description: 'Regular maintenance and cleaning services for optimal performance.'
    },
    {
      id: 11,
      src: '/images/gallery/11.jpeg',
      alt: 'Solar Energy Monitoring',
      category: 'storage',
      title: 'Smart Energy Management',
      description: 'Advanced monitoring system with mobile app control capabilities.'
    },
    {
      id: 12,
      src: '/images/gallery/12.jpeg',
      alt: 'Green Energy Solution',
      category: 'commercial',
      title: 'Sustainable Business Solution',
      description: 'Complete renewable energy solutions for businesses.'
    },
    {
      id: 13,
      src: '/images/gallery/13.jpeg',
      alt: 'Residential Solar Panels',
      category: 'residential',
      title: 'Family Home Solar Installation',
      description: '8kW residential solar system with net metering capabilities.'
    },
    {
      id: 14,
      src: '/images/gallery/14.jpeg',
      alt: 'Commercial Solar Installation',
      category: 'commercial',
      title: 'Factory Solar Power System',
      description: '1MW commercial solar installation for a manufacturing unit.'
    },
    {
      id: 15,
      src: '/images/gallery/15.jpeg',
      alt: 'Industrial Solar Farm',
      category: 'industrial',
      title: 'Large Scale Solar Project',
      description: '5MW industrial solar farm with grid integration capabilities.'
    },
    {
      id: 16,
      src: '/images/gallery/16.jpeg',
      alt: 'Solar Panel Array',
      category: 'residential',
      title: 'Modern Home Solar Setup',
      description: 'Sleek solar panel installation on a modern home.'
    },
    {
      id: 20,
      src: '/images/gallery/20.jpeg',
      alt: 'Rooftop Solar System',
      category: 'residential',
      title: 'Villa Solar Installation',
      description: 'Premium villa solar system with smart monitoring capabilities.'
    },
    {
      id: 21,
      src: '/images/gallery/21.jpeg',
      alt: 'Solar Installation Work',
      category: 'residential',
      title: 'Professional Solar Installation',
      description: 'Skilled technicians installing solar panels safely.'
    },
    {
      id: 22,
      src: '/images/gallery/22.jpeg',
      alt: 'Solar Panel Cleaning',
      category: 'maintenance',
      title: 'Solar Panel Maintenance Service',
      description: 'Professional cleaning and maintenance of solar panels.'
    },
    {
      id: 23,
      src: '/images/gallery/23.jpeg',
      alt: 'Solar Installation Project',
      category: 'residential',
      title: 'New Solar Installation',
      description: 'Latest solar panel installation project completed successfully with high efficiency.'
    },
    {
      id: 24,
      src: '/images/gallery/24.jpeg',
      alt: 'Solar Energy System',
      category: 'commercial',
      title: 'Commercial Solar Project',
      description: 'Large-scale solar energy system installation for commercial use.'
    }
  ];

  const categories = [
    { key: 'all', label: 'Our Projects' }
  ];

  const filteredImages = galleryImages;

  const openLightbox = (image) => {
    setSelectedImage(image);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'unset';
  };

  const nextImage = () => {
    const currentIndex = filteredImages.findIndex(img => img.id === selectedImage.id);
    const nextIndex = (currentIndex + 1) % filteredImages.length;
    setSelectedImage(filteredImages[nextIndex]);
  };

  const prevImage = () => {
    const currentIndex = filteredImages.findIndex(img => img.id === selectedImage.id);
    const prevIndex = (currentIndex - 1 + filteredImages.length) % filteredImages.length;
    setSelectedImage(filteredImages[prevIndex]);
  };

  const handleKeyPress = (e) => {
    if (!selectedImage) return;
    
    if (e.key === 'Escape') {
      closeLightbox();
    } else if (e.key === 'ArrowRight') {
      nextImage();
    } else if (e.key === 'ArrowLeft') {
      prevImage();
    }
  };

  React.useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [selectedImage]);

  return (
    <>
      <Helmet>
        <title>Solar Installation Gallery - Our Solar Projects Portfolio</title>
        <meta name="description" content="Explore our portfolio of residential and commercial solar installations. See examples of our high-quality solar projects and energy solutions." />
        <meta name="keywords" content="solar gallery, solar projects, residential solar, commercial solar, solar installations, solar portfolio" />
      </Helmet>

      <div className="gallery-page">
        {/* Hero Section */}
        <section className="gallery-hero">
          <div className="gallery-hero-overlay"></div>
          <div className="gallery-hero-content">
            <h1 className="gallery-hero-title">Gallery</h1>
          </div>
        </section>

        {/* Gallery Grid */}
        <section className="section gallery-grid-section">
          <div className="container">
            {/* Section Header */}
            <div className="gallery-section-header">
              <h2>Our Projects</h2>
            </div>
            
            <div className="gallery-grid">
              {filteredImages.map((image) => (
                <div
                  key={image.id}
                  className="gallery-item"
                  onClick={() => openLightbox(image)}
                >
                  <img src={image.src} alt={image.alt} loading="lazy" />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Lightbox Modal */}
        {selectedImage && (
          <div className="lightbox-overlay" onClick={closeLightbox}>
            <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
              <button className="lightbox-close" onClick={closeLightbox}>
                <i className="fas fa-times"></i>
              </button>
              
              <button className="lightbox-nav lightbox-prev" onClick={prevImage}>
                <i className="fas fa-chevron-left"></i>
              </button>
              
              <button className="lightbox-nav lightbox-next" onClick={nextImage}>
                <i className="fas fa-chevron-right"></i>
              </button>

              <div className="lightbox-image-container">
                <img src={selectedImage.src} alt={selectedImage.alt} />
              </div>
            </div>
          </div>
        )}


        {/* CTA Section */}
        <section className="section cta-section">
          <div className="container">
            <div className="cta-content text-center">
              <h2>Ready to Go Solar?</h2>
              <p>
                Join thousands of satisfied customers who have made the switch to clean, renewable solar energy.
                Get your free consultation today.
              </p>
              <div className="cta-buttons">
                <a href="/contact" className="btn btn-primary">
                  Get Free Quote
                </a>
                <a href="/services" className="btn btn-outline">
                  Our Solar Services
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Gallery;